// =============================================
// docDraftStore — client for per-user document form drafts (/api/dms/draft)
// =============================================
// The Process Docs/Receipts Filing form autosaves its unsaved field values to the
// SERVER (dms_document_draft, migration 109) so they survive a document switch,
// reload, closed browser, cleared browser data or another device — until the
// document is filed. Nothing is kept in browser storage.
//
// All calls are best-effort: a draft is a safety net, so a failed save/clear never
// blocks or interrupts the user (the form itself still holds the values).
// Writes are serialized so a slow save can't land after a later clear and
// resurrect a draft that was just filed or discarded.
// =============================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function authToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

export interface DocDraftRow<T> {
  document_id: number;
  data: T;
  updated_at: string;
}

let writeChain: Promise<unknown> = Promise.resolve();
function enqueue<T>(op: () => Promise<T>): Promise<T> {
  const next = writeChain.then(op, op);
  writeChain = next.catch(() => undefined);
  return next;
}

/** Every live draft the logged-in user has for `form`. Empty when offline / logged out. */
export async function listDocDrafts<T>(form: string): Promise<DocDraftRow<T>[]> {
  const token = authToken();
  if (!token) return [];
  try {
    const res = await fetch(`${API_BASE}/dms/drafts?form=${encodeURIComponent(form)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return [];
    const json = await res.json();
    return Array.isArray(json)
      ? json.map((r: any) => ({ document_id: Number(r.document_id), data: r.data as T, updated_at: r.updated_at }))
      : [];
  } catch {
    return [];
  }
}

/**
 * Save (replace) the user's draft for a document. `keepalive` lets the request
 * outlive the page — used for the last save when the tab is closing.
 */
export function saveDocDraft<T extends object>(
  documentId: number,
  form: string,
  data: T,
  keepalive = false,
): Promise<boolean> {
  const token = authToken();
  if (!token) return Promise.resolve(false);
  const send = async () => {
    try {
      const res = await fetch(`${API_BASE}/dms/draft/${documentId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ form, data }),
        keepalive,
      });
      return res.ok;
    } catch {
      return false;
    }
  };
  // A closing page can't wait behind the queue — send it now.
  return keepalive ? send() : enqueue(send);
}

/** Clear the user's draft for a document (it was filed, or the draft was discarded). */
export function clearDocDraft(documentId: number, form: string): Promise<boolean> {
  const token = authToken();
  if (!token) return Promise.resolve(false);
  return enqueue(async () => {
    try {
      const res = await fetch(`${API_BASE}/dms/draft/${documentId}?form=${encodeURIComponent(form)}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.ok;
    } catch {
      return false;
    }
  });
}
